import { graphql, useStaticQuery } from 'gatsby'
import { decode } from 'html-entities'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import React from 'react'
import { useDataLayer } from '../../hooks/useDataLayer'
import DynamicLink from '../atoms/DynamicLink'

const M23JobListAbstract = ({
  section1Heading,
  section1Button1,
  section2Heading,
  section2Button1,
  anchorLinkId,
}) => {
  const dataLayer = useDataLayer()
  const isSingle = !section2Heading
  const {
    jobs: { nodes: jobResults },
    apprenticeships: { nodes: apprenticeshipResults },
  } = useStaticQuery(graphql`
    {
      jobs: allJobsXml {
        nodes {
          name
          jobs: xmlChildren {
            children {
              name
              content
            }
          }
        }
      }
      apprenticeships: allApprenticeshipsXml(filter: { name: { eq: "jobs" } }) {
        nodes {
          name
          jobs: xmlChildren {
            children {
              name
              content
            }
          }
        }
      }
    }
  `)

  const jobNodes = orderBy(
    jobResults?.find(j => j.name === 'jobs').jobs,
    job => {
      const parts = job.children
        .find(node => node.name === 'date')
        .content.split(' ')
      return moment(`${parts[3]}-${parts[2]}-${parts[1]} 00:00:00`)
    },
    ['desc'],
  )

  const apprenticeshipNodes = orderBy(
    apprenticeshipResults[0].jobs,
    job => {
      const parts = job.children
        .find(node => node.name === 'created')
        .content.split(' ')
      return moment(`${parts[3]}-${parts[2]}-${parts[1]} 00:00:00`)
    },
    ['desc'],
  )

  const jobs = jobNodes
    .map(job =>
      Object.values(job.children).reduce(
        (acc, cur) => ({ ...acc, [cur.name]: cur.content }),
        {},
      ),
    )
    .filter(job => job.company.indexOf('AS Aufz&#252;ge AG') !== -1)

  const apprenticeships = apprenticeshipNodes.map(job =>
    Object.values(job.children).reduce(
      (acc, cur) => ({ ...acc, [cur.name]: cur.content }),
      {},
    ),
  )

  return (
    <section className="my-20 md:my-28" id={anchorLinkId}>
      <div className="container">
        <div
          className={`${isSingle ? 'space-y-8' : 'space-y-20'} md:space-y-0 ${
            isSingle ? 'row-type-e-start' : 'row-type-a-start'
          }`}
        >
          <div className={`${isSingle ? 'col-1-type-e' : 'col-1-type-a'}`}>
            <h2 className="mb-4 u-h2 lg:mb-6 xl:mb-5">{section1Heading}</h2>

            {!isSingle && (
              <div className="column-indent">
                <div className="mb-6 space-y-5">
                  {jobs.slice(0, 3).map(job => (
                    <div key={job.ID}>
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          dataLayer.push({
                            event: 'userinteraction',
                            eventCategory: 'Offene Stellen',
                            eventAction: 'click',
                            eventLabel: decode(job.title),
                            event_location: null,
                          })
                        }}
                      >
                        <div className="mb-1 underline u-p hover:text-red-as">
                          {decode(job.title)}
                        </div>
                        <div className="font-bold u-p-small">
                          {decode(job.city)}
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
                <DynamicLink link={section1Button1} className="u-btn" />
              </div>
            )}
          </div>
          <div className={`${isSingle ? 'col-2-type-e' : 'col-2-type-a'}`}>
            {section2Heading && (
              <h2 className="mb-4 u-h2 lg:mb-6 xl:mb-5">{section2Heading}</h2>
            )}

            {isSingle ? (
              <div className="column-indent">
                <div className="mb-6 space-y-5">
                  {jobs.slice(0, 3).map(job => (
                    <div key={job.ID}>
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="mb-1 underline u-p hover:text-red-as">
                          {decode(job.title)}
                        </div>
                        <div className="font-bold u-p-small">
                          {decode(job.city)}
                        </div>
                      </a>
                    </div>
                  ))}
                </div>

                <DynamicLink link={section1Button1} className="u-btn" />
              </div>
            ) : (
              <div className="column-indent">
                <div className="mb-6 space-y-5">
                  {apprenticeships.slice(3, 6).map(job => (
                    <div key={job.ID}>
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="mb-1 underline u-p hover:text-red-as">
                          {decode(job.title)}
                        </div>
                        <div className="font-bold u-p-small">
                          {decode(job.city)}
                        </div>
                      </a>
                    </div>
                  ))}
                </div>

                <DynamicLink link={section2Button1} className="u-btn" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default M23JobListAbstract
